import { defineStore } from 'pinia'   
import { computed, ref, Ref } from 'vue'
import { useUserStore } from './user';
import { UserDependentStore } from './UserDependentStore'

import { DEBUG, DEBUG_WARN, EquipmentApi } from '@/util'
import { IEquipmentMasterDataWithBomWarningCount } from '@/types/equipment';

/** store (cache) of "all devices" for the currently active ADMIN user  */
export const useAdminEquipmentsStore = defineStore('adminEquipmentsStore', () => {
    const userStore = useUserStore()

    /********************************************************************************************************************
     STATE
     ********************************************************************************************************************/
    const _devices: Ref<IEquipmentMasterDataWithBomWarningCount[] | null> = ref(null)

    const state = { _devices }

    /********************************************************************************************************************
     GETTERS
     ********************************************************************************************************************/
    const devices = computed(() => _devices.value)

    const getters = { devices }

    /********************************************************************************************************************
     ACTIONS
     ********************************************************************************************************************/
    /** Reloads the currently active list of "all devices" */
    function refresh(): Promise<IEquipmentMasterDataWithBomWarningCount[]> {
        if (userStore.isAdmin) {
            const api = new EquipmentApi
            return api.getAllEquipments()
                .then(response => {
                    _devices.value = response.data
                    refreshBomWarningCounts()

                    return _devices.value
                })
        }
        else {
            return new Promise(() => [])
        }
    }

    function reset() {
        DEBUG('resetting store: Admin equipments')
        _devices.value = null
    }

    const actions = { refresh, reset }

    /********************************************************************************************************************
     PRIVATE
     ********************************************************************************************************************/
    function refreshBomWarningCounts() {
        const api = new EquipmentApi
        const currentDevices = _devices.value;

        // now load BOM warning counts
        api.getEquipmentBomWarningCounts()
            .then(response => {
                const equipmentDict = response.data

                currentDevices?.forEach(device => {
                    const warningCount = equipmentDict[device.equipment.equipmentNo]
                    device.bomWarningCount = warningCount ?? 0
                })   

                _devices.value = currentDevices // re-active
            })
            .catch(error => DEBUG_WARN('AdminEquipmentsStore: Failed to load BOM warning counts:', error))
    }

    /********************************************************************************************************************
     INIT
     ********************************************************************************************************************/
    new UserDependentStore(() => refresh(), () => reset())

    /********************************************************************************************************************
     MISC
     ********************************************************************************************************************/
    // Setup dependency on user store
    return { ...state, ...getters, ...actions  }
})